import React, { useState, useRef, useEffect } from 'react';
import { View, Text } from '@adobe/react-spectrum';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './QuillStyles.scss';
import styles from '../FormField.module.scss';
import Checkmark from '@spectrum-icons/workflow/Checkmark';

// Import Quill modules
import Quill from 'quill';
const Delta = Quill.import('delta');

interface QuillEditorProps {
  value: string;
  onChange: (content: string) => void;
  placeholder?: string;
  // null/undefined/<=0 => unlimited
  charLimit?: number | null;
  className?: string;
  showCharCount?: boolean;
  minHeight?: string;
  toolbarOptions?: any;
  footerLeft?: React.ReactNode;
  footerCenter?: React.ReactNode;
  footerRight?: React.ReactNode;
  onCharCountChange?: (count: number) => void;
  autoFocus?: boolean;
  readOnly?: boolean; // RBAC: Make editor read-only
}

/**
 * Core Quill editor component that can be reused across different contexts
 */
export const QuillEditor: React.FC<QuillEditorProps> = ({
  value = '',
  onChange,
  placeholder = 'Enter text here...',
  charLimit = null,
  className = '',
  showCharCount = true,
  minHeight = '200px',
  toolbarOptions,
  footerLeft,
  footerCenter,
  footerRight,
  onCharCountChange,
  autoFocus = false,
  readOnly = false
}) => {
  const quillRef = useRef<ReactQuill>(null);
  const [editorHtml, setEditorHtml] = useState(value);
  const [charCount, setCharCount] = useState(0);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  // Initialize character count and set up character limit handler
  useEffect(() => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();

      const hasLimit =
        Number.isFinite(charLimit as number) && (charLimit as number) > 0;
      const limit = hasLimit ? (charLimit as number) : Number.POSITIVE_INFINITY;

      // Calculate initial character count
      const text = quill.getText();
      // Subtract 1 to account for Quill's automatic trailing newline
      const initialCount = text.length > 0 ? text.length - 1 : 0;
      setCharCount(initialCount);
      onCharCountChange?.(initialCount);

      // Set up text-change event handler to enforce character limit
      quill.on('text-change', function (delta, oldContents, source) {
        if (source === 'user') {
          setHasInteracted(true);
        }

        if (source !== 'user') return;

        const text = quill.getText();
        // Subtract 1 to account for Quill's automatic trailing newline
        const currentLength = text.length > 0 ? text.length - 1 : 0;

        if (hasLimit && currentLength > limit) {
          // Get the current cursor position
          const cursorPosition =
            quill.getSelection()?.index || quill.getLength();

          // Delete the excess characters
          quill.deleteText(limit, currentLength);

          // Move cursor to end of allowed text
          quill.setSelection(limit, limit);

          setCharCount(limit);
          onCharCountChange?.(limit);
        } else {
          setCharCount(currentLength);
          onCharCountChange?.(currentLength);
        }
      });

      // Set up focus/blur event handlers for styling
      quill.on('selection-change', function (range, oldRange, source) {
        if (range) {
          setIsFocused(true);
        } else {
          setIsFocused(false);
        }
      });

      // Handle link clicks in the Quill editor
      const handleQuillLinkClick = (event: Event) => {
        const target = event.target as HTMLElement;

        if (target.tagName === 'A') {
          const anchor = target as HTMLAnchorElement;
          const href = anchor.getAttribute('href');

          if (href) {
            event.preventDefault();
            event.stopPropagation();

            // Check if it's already an external link (case-insensitive)
            if (/^https?:\/\//i.test(href)) {
              window.open(href, '_blank', 'noopener,noreferrer');
            } else if (href.startsWith('mailto:') || href.startsWith('tel:')) {
              window.location.href = href;
            } else {
              // For all other links (including domain names without protocol), add https://
              const urlToOpen =
                href.startsWith('www.') || href.includes('.')
                  ? `https://${href}`
                  : href;
              window.open(urlToOpen, '_blank', 'noopener,noreferrer');
            }
          }
        }
      };

      // Apply CSS to ensure editor content stays within bounds and height
      const editorRoot = quillRef.current.getEditingArea() as HTMLElement;
      if (editorRoot) {
        editorRoot.style.width = '100%';
        editorRoot.style.maxWidth = '100%';
        editorRoot.style.overflowWrap = 'break-word';
        editorRoot.style.wordWrap = 'break-word';
        editorRoot.style.wordBreak = 'break-word';
        editorRoot.style.minHeight = String(minHeight);
      }

      // Add click event listener to the Quill editor container
      const editorContainer = quill.container;
      if (editorContainer) {
        // Ensure the container also respects the requested min height
        (editorContainer as HTMLElement).style.minHeight = String(minHeight);
        editorContainer.addEventListener('click', handleQuillLinkClick, true);

        // Also add to the editing area specifically
        const editingArea = editorContainer.querySelector('.ql-editor');
        if (editingArea) {
          editingArea.addEventListener('click', handleQuillLinkClick, true);
        }

        // Set up tooltip positioning to always center inside the editor's visible viewport
        const fixTooltipPosition = () => {
          const tooltip = quill.container.querySelector(
            '.ql-tooltip'
          ) as HTMLElement;
          if (tooltip) {
            // Get the editor element (the scrollable area)
            const editor = quill.root;

            // Get the scroll position and visible height
            const scrollTop = editor.scrollTop;
            const visibleHeight = editor.clientHeight;

            // Calculate the center of the visible viewport
            const centerY = scrollTop + visibleHeight / 2;

            // Position tooltip at the center of the visible area
            tooltip.style.left = '50%';
            tooltip.style.top = `${centerY}px`;
            tooltip.style.right = 'auto';
            tooltip.style.bottom = 'auto';
            tooltip.style.transform = 'translate(-50%, -50%)';
            tooltip.style.position = 'absolute';
          }
        };

        // Set up mutation observer to watch for tooltip changes
        const observer = new MutationObserver(mutations => {
          mutations.forEach(mutation => {
            if (
              mutation.type === 'childList' ||
              mutation.type === 'attributes'
            ) {
              const tooltip = quill.container.querySelector(
                '.ql-tooltip'
              ) as HTMLElement;
              if (tooltip && tooltip.style.display !== 'none') {
                // Small delay to ensure tooltip is fully rendered
                setTimeout(fixTooltipPosition, 10);
              }
            }
          });
        });

        // Add scroll event listener to reposition tooltip when scrolling
        const editor = quill.root;
        const handleScroll = () => {
          const tooltip = quill.container.querySelector(
            '.ql-tooltip'
          ) as HTMLElement;
          if (tooltip && tooltip.style.display !== 'none') {
            fixTooltipPosition();
          }
        };
        editor.addEventListener('scroll', handleScroll);

        // Start observing the quill container for tooltip changes
        observer.observe(quill.container, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['style', 'class']
        });

        // Cleanup function will remove these listeners
        return () => {
          observer.disconnect();
          editor.removeEventListener('scroll', handleScroll);
          editorContainer.removeEventListener(
            'click',
            handleQuillLinkClick,
            true
          );
          if (editingArea) {
            editingArea.removeEventListener(
              'click',
              handleQuillLinkClick,
              true
            );
          }
        };
      }
    }
  }, [charLimit, onCharCountChange, minHeight]);

  // Auto-focus the editor on mount if autoFocus is true
  useEffect(() => {
    if (autoFocus && quillRef.current) {
      const quill = quillRef.current.getEditor();
      // Use setTimeout to ensure the editor is fully mounted
      setTimeout(() => {
        quill.focus();
      }, 100);
    }
  }, [autoFocus]);

  // Handle content change
  const handleChange = (html: string) => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      const text = quill.getText();
      // Subtract 1 to account for Quill's automatic trailing newline
      const count = text.length > 0 ? text.length - 1 : 0;

      const hasLimit =
        Number.isFinite(charLimit as number) && (charLimit as number) > 0;
      const limit = hasLimit ? (charLimit as number) : Number.POSITIVE_INFINITY;

      if (!hasLimit || count <= limit) {
        setEditorHtml(html);
        onChange(html);
        onCharCountChange?.(count);
      }
    }
  };

  // Define default Quill modules/formats if not provided
  const defaultModules = {
    toolbar: {
      container: toolbarOptions || [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: [] }],
        ['link'],
        ['clean']
      ]
    },
    clipboard: {
      // Preserve formatting on paste but filter some things
      matchVisual: true
    }
  };

  const formats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'color',
    'background',
    'list',
    'bullet',
    'align',
    'link'
  ];

  // Display text for character count
  const hasLimit =
    Number.isFinite(charLimit as number) && (charLimit as number) > 0;
  const limitNum = hasLimit ? (charLimit as number) : undefined;
  const shouldShowCharCount = hasLimit && showCharCount;
  const characterCountText = shouldShowCharCount
    ? hasInteracted || value.length > 0
      ? `Characters remaining: ${Math.max(0, (limitNum as number) - charCount)}`
      : `Limit: ${limitNum} Characters`
    : '';

  return (
    <div className={`quill-editor-container ${className}`}>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={editorHtml}
        onChange={handleChange}
        modules={defaultModules}
        formats={formats}
        placeholder={placeholder}
        readOnly={readOnly}
        style={{
          width: '100%',
          maxWidth: '100%',
          minHeight: minHeight,
          border: isFocused ? '2px solid #d5d5d5' : '1px solid #f5f5f5',
          borderRadius: '6px'
        }}
      />
      {((typeof footerLeft !== 'string' && !!footerLeft) ||
        (shouldShowCharCount && typeof footerLeft === 'string') ||
        footerCenter ||
        footerRight) && (
        <div className={styles.editorFooterRow}>
          <div className={styles.footerLeft}>
            {typeof footerLeft === 'string'
              ? shouldShowCharCount && (
                  <span className={styles.characterCount}>
                    {characterCountText}
                  </span>
                )
              : footerLeft}
          </div>
          <div className={styles.footerCenter}>{footerCenter}</div>
          <div className={styles.footerRight}>{footerRight}</div>
        </div>
      )}
    </div>
  );
};
